import {FC, Fragment} from 'react';
import {Rolle} from 'features/rettsstiftelse/model/losore/rolle';
import {useTranslation} from 'react-i18next';
import {StringUtils} from 'common/utils/string-utils';
import {RolleAdresse} from 'features/rettsstiftelse/components/rolle-adresse';
import {DetailGroup} from 'common/components/detail-group';
import {DetailRow} from 'common/components/detail-row';
import {Rollegruppetype, RollegruppetypeOrder} from 'features/rettsstiftelse/rollegruppetype';
import {ScreenReaderUtils} from 'common/utils/screenreader-utils';
import {ConceptID, ConceptIDKey} from 'features/terms/felles-datakatalog/concept-ids';
import {AktorType} from 'features/rettsstiftelse/constants/aktor-type';
import {FdkText} from 'features/terms/components/fdk-text';
import {getEnv} from 'environment/public.environment';
import {rolletypeSortValue} from 'features/rettsstiftelse/rolletype';
import {groupBy} from 'common/utils';
import {VirksomhetAktor} from 'features/rettsstiftelse/model/aktor';
import {Rettsstiftelse} from 'features/rettsstiftelse/model/losore/rettsstiftelse';
import {createUseStyles} from 'react-jss';
import {useLang} from 'features/i18n/use-lang';

const useRolleDetailGroupStyles = createUseStyles({
  orgnummerLink: {
    textWrap: 'nowrap',
  },
});

const OrgnrLink: FC<{rolle: Rolle}> = ({rolle}) => {
  const styles = useRolleDetailGroupStyles();
  const {t} = useTranslation(['rettsstiftelser']);
  const orgnr = (rolle.rolleinnehaver as VirksomhetAktor).organisasjonsnummer;

  return (
    <div>
      {t('rettsstiftelser:organisasjonsnummer')}:{' '}
      <a
        href={`${getEnv().links.brreg.virksomhetsopplysninger}/enheter?navn=${orgnr}`}
        className={styles.orgnummerLink}
      >
        {ScreenReaderUtils.orgnr(orgnr)}
      </a>
    </div>
  );
};

const RolleDetail: FC<Rolle> = rolle => {
  const description = Object.keys(ConceptID).includes(rolle.rolletype.split('.')[1].toUpperCase()) ? (
    <FdkText conceptId={ConceptID[rolle.rolletype.split('.')[1].toUpperCase() as ConceptIDKey]} />
  ) : undefined;

  return (
    <DetailRow
      key={rolle.rolletype + '-' + rolle.rolleinnehaver.navn}
      label={rolle.rolletypeBeskrivelse}
      description={description}
    >
      {rolle.rolleinnehaver.navn}
      {rolle.rolleinnehaver.aktorType === AktorType.VIRKSOMHET && <OrgnrLink rolle={rolle} />}
      <RolleAdresse rolleInnehaver={rolle.rolleinnehaver} />
    </DetailRow>
  );
};

const OppretterExtras: FC<{rettsstiftelse: Rettsstiftelse}> = ({rettsstiftelse}) => {
  const {t} = useTranslation(['rettsstiftelser']);
  const appLanguage = useLang();

  const isGjeldsOrdning = !!rettsstiftelse.gjeldsordning;
  const showBeslutningsTidspunkt =
    !!rettsstiftelse.beslutningstidspunkt?.length &&
    (isGjeldsOrdning ? rettsstiftelse.gjeldsordning?.type === 'gjeldsordningstype.forhandling' : true);

  return (
    <>
      {showBeslutningsTidspunkt && (
        <DetailRow
          label={t('rettsstiftelser:beslutningstidspunkt', {context: rettsstiftelse.type.split('.')[1]})}
          description={<FdkText conceptId={ConceptID.BESLUTNINGSTIDSPUNKT} />}
        >
          {StringUtils.formatDateTime(new Date(rettsstiftelse.beslutningstidspunkt as string), appLanguage, t, true)}
        </DetailRow>
      )}
      {rettsstiftelse.gjeldsordning?.saksnummerNamsmann && (
        <DetailRow label={t('rettsstiftelser:namsmannensSaksnummer')}>
          {rettsstiftelse.gjeldsordning?.saksnummerNamsmann}
        </DetailRow>
      )}
    </>
  );
};

interface RolleDetailGroupProps {
  rettsstiftelse: Rettsstiftelse;
  id: string;
}

export const RolleDetailGroup: FC<RolleDetailGroupProps> = ({rettsstiftelse, id}) => {
  const hasOppretter = !!rettsstiftelse.roller.find(rolle => rolle.rollegruppetype === Rollegruppetype.OPPRETTER);

  return (
    <DetailGroup id={id}>
      {!hasOppretter && <OppretterExtras rettsstiftelse={rettsstiftelse} />}
      {Object.entries(groupBy(rettsstiftelse.roller, 'rollegruppetype'))
        .sort((a, b) => RollegruppetypeOrder[a[0]] - RollegruppetypeOrder[b[0]])
        .map(([rollegruppetype, rollerOfType]) => {
          return (
            <Fragment key={rollegruppetype}>
              {rollerOfType
                .sort((a, b) => rolletypeSortValue(a.rolletype) - rolletypeSortValue(b.rolletype))
                .map((rolle, i) => (
                  <RolleDetail key={i} {...rolle} />
                ))}
              {rollegruppetype === Rollegruppetype.OPPRETTER && <OppretterExtras rettsstiftelse={rettsstiftelse} />}
            </Fragment>
          );
        })}
    </DetailGroup>
  );
};
